import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const READER_TOKEN_KEY = "pv_reader_token";

const ReaderAuthContext = createContext(null);

export function ReaderAuthProvider({ children }) {
    const [reader, setReader] = useState(null);
    const [checking, setChecking] = useState(true);

    const bootstrap = useCallback(async () => {
        setChecking(true);
        const token = localStorage.getItem(READER_TOKEN_KEY);
        if (!token) { setReader(null); setChecking(false); return; }
        try {
            const res = await fetch(`${API_ROOT}/reader/me`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: "include",
            });
            if (res.ok) {
                const data = await res.json();
                setReader(data);
            } else {
                localStorage.removeItem(READER_TOKEN_KEY);
                setReader(null);
            }
        } catch {
            setReader(null);
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => { bootstrap(); }, [bootstrap]);

    const login = useCallback(async (email, password) => {
        const res = await fetch(`${API_ROOT}/reader/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ email, password }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || "Credenziali non valide");
        }
        const data = await res.json();
        localStorage.setItem(READER_TOKEN_KEY, data.token);
        setReader(data.reader);
        return data.reader;
    }, []);

    const register = useCallback(async (payload) => {
        const res = await fetch(`${API_ROOT}/reader/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const msg = typeof err.detail === "string" ? err.detail :
                Array.isArray(err.detail) ? err.detail.map(d => d.msg).join("; ") :
                "Errore registrazione";
            throw new Error(msg);
        }
        const data = await res.json();
        localStorage.setItem(READER_TOKEN_KEY, data.token);
        setReader(data.reader);
        return data.reader;
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch(`${API_ROOT}/reader/logout`, {
                method: "POST", credentials: "include",
            });
        } catch (_) { /* ignore logout errors */ }
        localStorage.removeItem(READER_TOKEN_KEY);
        setReader(null);
    }, []);

    const forgotPassword = useCallback(async (email) => {
        const res = await fetch(`${API_ROOT}/reader/forgot-password`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || "Errore invio email");
        }
        return await res.json();
    }, []);

    return (
        <ReaderAuthContext.Provider
            value={{ reader, checking, login, register, logout, forgotPassword, refresh: bootstrap }}
        >
            {children}
        </ReaderAuthContext.Provider>
    );
}

export function useReaderAuth() {
    const ctx = useContext(ReaderAuthContext);
    if (!ctx) throw new Error("useReaderAuth deve essere usato dentro ReaderAuthProvider");
    return ctx;
}

export function readerFetch(path, options = {}) {
    const token = localStorage.getItem(READER_TOKEN_KEY);
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (!headers["Content-Type"] && options.body && !(options.body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
    }
    return fetch(`${API_ROOT}${path}`, {
        credentials: "include",
        ...options,
        headers,
    });
}
